import React, { useState, useEffect } from 'react'
import Button from '@/components/ui/Button'
import Tag from '@/components/ui/Tag'
import SidePanel from '@/components/ui/SidePanel'
import { designTokens } from '@/utils/design-tokens'
import { PerformanceClassService } from '@/api/generated/services/PerformanceClassService'
import { PerformanceClassCreateRequest } from '@/api/generated/models/PerformanceClassCreateRequest'
import { useAlert } from '@/components/ui/Alert/exports'
import { handleApiError, ErrorMessages } from '@/utils/api-error-handler'
import RequiredLabel from '@/components/ui/RequiredLabel'

interface PerformanceClassCreatePanelProps {
  isOpen: boolean
  onClose: () => void
  categoryId: string
  categoryName: string
  onSuccess?: () => void
}

// Section Header Component
const SectionHeader: React.FC<{ title: string }> = ({ title }) => (
  <div
    className="pb-2 border-b"
    style={{
      borderBottomColor: '#EBEBEB',
      borderBottomWidth: '1px',
      padding: '7px 0px'
    }}
  >
    <h3
      style={{
        fontFamily: designTokens.typography.fontFamily.sans[0],
        fontSize: '18px',
        fontWeight: 700,
        color: designTokens.colors.gray[6]
      }}
    >
      {title}
    </h3>
  </div>
)

// Info Field Component
const InfoField: React.FC<{
  label: string | React.ReactNode,
  value: string | React.ReactNode,
  fullWidth?: boolean
}> = ({ label, value, fullWidth = false }) => (
  <div className={`flex ${fullWidth ? 'flex-col gap-2' : 'items-center gap-5'}`}>
    <div className={fullWidth ? '' : 'w-37 flex-shrink-0'}>
      <span
        style={{
          fontFamily: designTokens.typography.fontFamily.sans[0],
          fontSize: '16px',
          fontWeight: 400,
          color: designTokens.colors.gray[6]
        }}
      >
        {label}
      </span>
    </div>
    <div className={fullWidth ? '' : 'flex-1 py-2'}>
      {typeof value === 'string' ? (
        <span
          style={{
            fontFamily: designTokens.typography.fontFamily.sans[0],
            fontSize: '16px',
            fontWeight: 400,
            color: designTokens.colors.gray[6]
          }}
        >
          {value}
        </span>
      ) : (
        value
      )}
    </div>
  </div>
)

const PerformanceClassCreatePanel: React.FC<PerformanceClassCreatePanelProps> = ({
  isOpen,
  onClose,
  categoryId,
  categoryName,
  onSuccess
}) => {
  const { showError } = useAlert()

  // Form state
  const [className, setClassName] = useState('')
  const [targetFigureFormula, setTargetFigureFormula] = useState('')
  const [doneFigureFormula, setDoneFigureFormula] = useState('')
  const [manualWeight, setManualWeight] = useState<number | null>(null)
  const [isManualWeight, setIsManualWeight] = useState(false)

  // UI state
  const [isSubmitting, setIsSubmitting] = useState(false)

  // Reset form when panel opens/closes
  useEffect(() => {
    if (isOpen) {
      setClassName('')
      setTargetFigureFormula('')
      setDoneFigureFormula('')
      setManualWeight(null)
      setIsManualWeight(false)
    }
  }, [isOpen])

  const handleSubmit = async () => {
    if (!categoryId) {
      showError('하위분류가 선택되지 않았습니다.')
      return
    }

    if (!className.trim()) {
      showError('성과지표명을 입력해주세요.')
      return
    }

    setIsSubmitting(true)

    try {
      const createRequest: PerformanceClassCreateRequest = {
        performance_category_id: categoryId,
        name: className.trim(),
        // 수식 필드들은 선택사항
        ...(targetFigureFormula.trim() ? { target_figure_formula: targetFigureFormula.trim() } : {}),
        ...(doneFigureFormula.trim() ? { done_figure_formula: doneFigureFormula.trim() } : {}),
        // 수동 가중치가 설정된 경우에만 포함
        ...(isManualWeight && manualWeight !== null ? { manual_weight: manualWeight } : {}),
      }

      await PerformanceClassService.appsPerformanceViewsCreatePerformanceClass({
        requestBody: createRequest
      })

      // Success
      onSuccess?.()
      onClose()
    } catch (err) {
      console.error('Failed to create performance class:', err)
      handleApiError(err, showError, `성과지표 ${ErrorMessages.CREATE_FAILED}`)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleCancel = () => {
    if (isSubmitting) return
    onClose()
  }

  const isFormValid = className.trim()

  const headerActions = (
    <>
      <Button
        size="small"
        color="white"
        onClick={handleCancel}
        disabled={isSubmitting}
      >
        취소
      </Button>
      <Button
        size="small"
        color="dark"
        onClick={handleSubmit}
        disabled={isSubmitting || !isFormValid}
      >
        {isSubmitting ? '저장 중...' : '저장'}
      </Button>
    </>
  )

  return (
    <SidePanel
      isOpen={isOpen}
      onClose={handleCancel}
      title="성과지표 생성"
      width={800}
      headerActions={headerActions}
    >
      <div
        className="flex flex-col space-y-6"
        style={{
          padding: '24px',
        }}
      >

        {/* Section 1: 기본정보 */}
        <div className="space-y-4">
          <SectionHeader title="기본정보" />
          <div className="space-y-4" style={{ padding: '0px 4px' }}>
            <InfoField
              label="상위 분류"
              value={
                <Tag color="warning" size="small">
                  ● {categoryName}
                </Tag>
              }
            />
            <InfoField
              label="유형"
              value={
                <Tag color="success" size="small">
                  ● 성과지표
                </Tag>
              }
            />
          </div>
        </div>

        {/* Section 2: 성과지표 정보 */}
        <div className="space-y-4">
          <SectionHeader title="성과지표 정보" />
          <div className="space-y-4" style={{ padding: '0px 4px' }}>
            <InfoField
              label={<RequiredLabel>성과지표명</RequiredLabel>}
              fullWidth={true}
              value={
                <input
                  type="text"
                  value={className}
                  onChange={(e) => setClassName(e.target.value)}
                  placeholder="성과지표명을 입력하세요"
                  className="w-full p-2 border rounded"
                  style={{
                    borderColor: designTokens.colors.gray[1],
                    fontFamily: designTokens.typography.fontFamily.sans[0],
                    fontSize: '16px',
                    color: designTokens.colors.gray[6]
                  }}
                  disabled={isSubmitting}
                />
              }
            />

            {/* 목표수치 수식 */}
            <InfoField
              label="목표수치 수식"
              fullWidth={true}
              value={
                <input
                  type="text"
                  value={targetFigureFormula}
                  onChange={(e) => setTargetFigureFormula(e.target.value)}
                  placeholder="목표수치 수식을 입력하세요 (선택사항)"
                  className="w-full p-2 border rounded"
                  style={{
                    borderColor: designTokens.colors.gray[1],
                    fontFamily: designTokens.typography.fontFamily.sans[0],
                    fontSize: '16px',
                    color: designTokens.colors.gray[6]
                  }}
                  disabled={isSubmitting}
                />
              }
            />

            {/* 달성수치 수식 */}
            <InfoField
              label="달성수치 수식"
              fullWidth={true}
              value={
                <input
                  type="text"
                  value={doneFigureFormula}
                  onChange={(e) => setDoneFigureFormula(e.target.value)}
                  placeholder="달성수치 수식을 입력하세요 (선택사항)"
                  className="w-full p-2 border rounded"
                  style={{
                    borderColor: designTokens.colors.gray[1],
                    fontFamily: designTokens.typography.fontFamily.sans[0],
                    fontSize: '16px',
                    color: designTokens.colors.gray[6]
                  }}
                  disabled={isSubmitting}
                />
              }
            />

            {/* 수동 가중치 설정 */}
            <InfoField
              label="가중치 설정"
              fullWidth={true}
              value={
                <div className="space-y-3">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={isManualWeight}
                      onChange={(e) => {
                        setIsManualWeight(e.target.checked)
                        if (!e.target.checked) {
                          setManualWeight(null)
                        }
                      }}
                      disabled={isSubmitting}
                      className="rounded"
                    />
                    <span style={{
                      fontFamily: designTokens.typography.fontFamily.sans[0],
                      fontSize: '14px',
                      color: designTokens.colors.gray[6]
                    }}>
                      수동 가중치 사용
                    </span>
                  </label>

                  {isManualWeight && (
                    <input
                      type="number"
                      value={manualWeight || ''}
                      onChange={(e) => {
                        const value = e.target.value === '' ? null : parseFloat(e.target.value)
                        setManualWeight(value)
                      }}
                      placeholder="가중치를 입력하세요 (0-100)"
                      min="0"
                      max="100"
                      step="0.1"
                      className="w-full p-2 border rounded"
                      style={{
                        borderColor: designTokens.colors.gray[1],
                        fontFamily: designTokens.typography.fontFamily.sans[0],
                        fontSize: '16px',
                        color: designTokens.colors.gray[6]
                      }}
                      disabled={isSubmitting}
                    />
                  )}

                  <p style={{
                    fontFamily: designTokens.typography.fontFamily.sans[0],
                    fontSize: '12px',
                    color: designTokens.colors.gray[4]
                  }}>
                    {isManualWeight
                      ? '수동으로 설정된 가중치가 적용됩니다.'
                      : '자동으로 계산된 가중치가 적용됩니다.'
                    }
                  </p>
                </div>
              }
            />

          </div>
        </div>

        {/* 수식 입력 안내 */}
        <div>
          <SectionHeader title="수식 입력 안내" />
          <div
            className="flex flex-col gap-4 mt-4"
            style={{
              fontFamily: designTokens.typography.fontFamily.sans[0],
              fontSize: designTokens.typography.fontSize['body-small'][0],
              color: designTokens.colors.gray[6],
              lineHeight: '1.6'
            }}
          >
            <p style={{ color: designTokens.colors.gray[5] }}>
              목표수치 수식과 달성수치 수식은 아래 3가지 방법으로 입력할 수 있습니다.
            </p>

            {/* 1. 기본 계산 */}
            <div className="flex flex-col gap-1">
              <span style={{ fontWeight: 600 }}>1. 기본 계산 (공백)</span>
              <span style={{ color: designTokens.colors.gray[5] }}>
                수식 입력란을 비워두면 하위 세부지표 값의 합계(sum)로 자동 계산됩니다.
              </span>
            </div>

            {/* 2. 숫자 입력 */}
            <div className="flex flex-col gap-1">
              <span style={{ fontWeight: 600 }}>2. 숫자 입력</span>
              <span style={{ color: designTokens.colors.gray[5] }}>
                숫자만 입력하면 해당 값이 그대로 적용됩니다.
              </span>
              <code
                style={{
                  padding: '4px 8px',
                  backgroundColor: designTokens.colors.gray[0],
                  borderRadius: '4px',
                  fontSize: designTokens.typography.fontSize['body-xsmall'][0],
                  color: designTokens.colors.gray[6],
                  width: 'fit-content'
                }}
              >
                예: 400
              </code>
            </div>

            {/* 3. 수식 입력 */}
            <div className="flex flex-col gap-1">
              <span style={{ fontWeight: 600 }}>3. 수식 입력</span>
              <span style={{ color: designTokens.colors.gray[5] }}>
                하위 세부지표를 <code style={{ padding: '1px 4px', backgroundColor: designTokens.colors.gray[0], borderRadius: '2px' }}>$번호</code> 형태로 참조하여 수식을 작성합니다.
              </span>
              <div className="flex flex-col gap-1 mt-1">
                <code
                  style={{
                    padding: '4px 8px',
                    backgroundColor: designTokens.colors.gray[0],
                    borderRadius: '4px',
                    fontSize: designTokens.typography.fontSize['body-xsmall'][0],
                    color: designTokens.colors.gray[6],
                    width: 'fit-content'
                  }}
                >
                  예: $1 + 200 + $2 * 1.2
                </code>
                <code
                  style={{
                    padding: '4px 8px',
                    backgroundColor: designTokens.colors.gray[0],
                    borderRadius: '4px',
                    fontSize: designTokens.typography.fontSize['body-xsmall'][0],
                    color: designTokens.colors.gray[6],
                    width: 'fit-content'
                  }}
                >
                  예: ($1 + 10) * $2 + 30
                </code>
              </div>
            </div>

            {/* 입력 가능 값 */}
            <div
              className="flex flex-col gap-1"
              style={{
                padding: '8px 12px',
                backgroundColor: designTokens.colors.gray[0],
                borderRadius: '6px',
                borderLeft: `3px solid ${designTokens.colors.primary.DEFAULT}`
              }}
            >
              <span style={{ fontWeight: 600, fontSize: designTokens.typography.fontSize['body-xsmall'][0] }}>입력 가능한 값</span>
              <span style={{ color: designTokens.colors.gray[5], fontSize: designTokens.typography.fontSize['body-xsmall'][0] }}>
                숫자, 세부지표 참조($), 사칙연산자(+, -, *, /), 괄호( )
              </span>
            </div>
          </div>
        </div>
      </div>
    </SidePanel>
  )
}

export default PerformanceClassCreatePanel